import { getAppLanguage } from './i18n';
import { apiPost } from './api';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

export interface WorkerAiResponse {
  reply?: string;
  feature?: string;
  timestamp?: string;
  error?: any;
}

export interface ParsedFoodNutrition {
  foodName?: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  quantity?: string;
  notes?: string;
  rawReply: string;
}

export interface ParsedRehabExercise {
  name: string;
  category: 'rehab';
  description: string;
  purpose?: string;
  targetMuscles?: string;
  sets?: number;
  reps?: string;
  rest?: string;
  duration?: number;
  difficulty?: string;
  safetyNotes?: string;
  rawReply: string;
}

export const WORKER_ENDPOINT = 'https://curly-heart-73af.salim-zarepour.workers.dev/chat';

/**
 * Converts Persian/Arabic digits to standard English digits.
 */
export function convertPersianDigitsToEnglish(str: string): string {
  if (!str) return '';
  return str
    .replace(/[۰-۹]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 1776 + 48))
    .replace(/[٠-٩]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 1632 + 48));
}

/**
 * Parses a single numeric or range segment (e.g. "450-550 kcal" or "188") into an average number.
 */
function parseNumberFromSegment(segment: string): number {
  if (!segment) return 0;

  // 1. Check for range e.g. 450-550, 450–550, 450 تا 550, 450 to 550
  const rangeMatch = segment.match(/(\d+(?:\.\d+)?)\s*(?:-|–|—|تا|الی|to|\s+)\s*(\d+(?:\.\d+)?)/i);
  if (rangeMatch) {
    const v1 = parseFloat(rangeMatch[1]);
    const v2 = parseFloat(rangeMatch[2]);
    if (!isNaN(v1) && !isNaN(v2)) {
      return (v1 + v2) / 2;
    }
  }

  // 2. Check for single number
  const singleMatch = segment.match(/(\d+(?:\.\d+)?)/);
  if (singleMatch) {
    const val = parseFloat(singleMatch[1]);
    if (!isNaN(val)) return val;
  }

  return 0;
}

/**
 * Extracts a number from a line that may contain multiple segments (e.g. 100g reference vs Total portion).
 */
function extractValueFromLine(rawLine: string): number {
  if (!rawLine) return 0;

  // Split line by table cells '|', slashes '/', semicolons ';', or parentheses
  const segments = rawLine.split(/(?:\||;|\/|\(|\))/).map((s) => s.trim()).filter(Boolean);

  if (segments.length > 1) {
    // 1. Look for segment with 'کل', 'توتال', 'سرویس', 'بشقاب', 'پرس', 'مجموع'
    for (const seg of segments) {
      const lower = seg.toLowerCase();
      if (lower.includes('100g') || lower.includes('100 گرم') || lower.includes('۱۰۰ گرم')) {
        continue;
      }
      if (
        lower.includes('کل') ||
        lower.includes('توتال') ||
        lower.includes('total') ||
        lower.includes('سرویس') ||
        lower.includes('بشقاب') ||
        lower.includes('پرس') ||
        lower.includes('مجموع')
      ) {
        const val = parseNumberFromSegment(seg);
        if (val > 0) return val;
      }
    }

    // 2. Fallback to non-100g segments starting from rightmost segment
    for (let i = segments.length - 1; i >= 0; i--) {
      const lower = segments[i].toLowerCase();
      if (lower.includes('100g') || lower.includes('100 گرم') || lower.includes('۱۰۰ گرم')) {
        continue;
      }
      const val = parseNumberFromSegment(segments[i]);
      if (val > 0) return val;
    }
  }

  return parseNumberFromSegment(rawLine);
}

/**
 * Parses a Markdown table from the AI response to extract calories and macros.
 * Handles both Transposed Tables (Rows = Nutrients) and Standard Itemised Tables (Columns = Nutrients).
 */
function parseTableNutrition(textInEnglish: string): {
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  found: boolean;
} {
  const lines = textInEnglish.split(/\r?\n/);
  const tableLines = lines.filter((l) => {
    const trimmed = l.trim();
    return trimmed.includes('|') && trimmed.split('|').length >= 3;
  });

  if (tableLines.length < 2) {
    return { calories: 0, protein: 0, carbs: 0, fat: 0, found: false };
  }

  // 1. CHECK IF TABLE IS TRANSPOSED (Rows are Nutrients e.g. Row 1 = Calorie, Row 2 = Protein)
  let calRow = -1;
  let protRow = -1;
  let carbRow = -1;
  let fatRow = -1;

  for (let i = 0; i < tableLines.length; i++) {
    const line = tableLines[i].toLowerCase();
    if (/^[\s\|:-]+$/.test(line)) continue;

    const cells = line.split('|').map((c) => c.trim());
    const cell0 = (cells[1] || cells[0] || '').toLowerCase();

    if (calRow === -1 && (cell0.includes('کالری') || cell0.includes('kcal') || cell0.includes('انرژی') || cell0.includes('calor'))) {
      calRow = i;
    } else if (protRow === -1 && (cell0.includes('پروتئین') || cell0.includes('protein') || cell0.includes('پروتبوط') || cell0.includes('prot'))) {
      protRow = i;
    } else if (carbRow === -1 && (cell0.includes('کربوهیدرات') || cell0.includes('کربو') || cell0.includes('carb'))) {
      carbRow = i;
    } else if (fatRow === -1 && (cell0.includes('چربی') || cell0.includes('fat') || cell0.includes('lipid'))) {
      fatRow = i;
    }
  }

  if (calRow !== -1 || (protRow !== -1 && carbRow !== -1)) {
    const headerCells = tableLines[0].split('|').map((c) => c.trim());
    let targetCol = -1;

    // Search header cells for column corresponding to total portion (e.g. 400g / کل / بشقاب)
    for (let c = 1; c < headerCells.length; c++) {
      const h = headerCells[c].toLowerCase();
      if (!h) continue;
      const hNormalized = convertPersianDigitsToEnglish(h);
      if (hNormalized.includes('100g') || hNormalized.includes('100 گرم') || /\b100\b/.test(hNormalized)) {
        continue;
      }
      if (
        h.includes('کل') ||
        h.includes('توتال') ||
        h.includes('total') ||
        h.includes('سرویس') ||
        h.includes('بشقاب') ||
        h.includes('پرس') ||
        /\d+\s*g/.test(hNormalized) ||
        /\d+\s*گرم/.test(h)
      ) {
        targetCol = c;
        break;
      }
    }

    // Default to last non-empty column if no specific header matched
    if (targetCol === -1) {
      targetCol = headerCells.length - 1;
      if (!headerCells[targetCol] && targetCol > 1) {
        targetCol--;
      }
    }

    const extractValueFromRow = (rowIdx: number): number => {
      if (rowIdx === -1) return 0;
      const rowCells = tableLines[rowIdx].split('|').map((c) => c.trim());
      let cellText = rowCells[targetCol];
      if (!cellText && rowCells.length > 2) {
        cellText = rowCells[rowCells.length - 1] || rowCells[rowCells.length - 2];
      }
      return extractValueFromLine(cellText || '');
    };

    const calories = calRow !== -1 ? extractValueFromRow(calRow) : 0;
    const protein = protRow !== -1 ? extractValueFromRow(protRow) : 0;
    const carbs = carbRow !== -1 ? extractValueFromRow(carbRow) : 0;
    const fat = fatRow !== -1 ? extractValueFromRow(fatRow) : 0;

    if (calories > 0 || protein > 0 || carbs > 0 || fat > 0) {
      return {
        calories: Math.round(calories),
        protein: parseFloat(protein.toFixed(1)),
        carbs: parseFloat(carbs.toFixed(1)),
        fat: parseFloat(fat.toFixed(1)),
        found: true,
      };
    }
  }

  // 2. STANDARD ITEMISED TABLE (Columns = Nutrients, Rows = Items)
  let headerIdx = -1;
  let calCol = -1;
  let carbCol = -1;
  let protCol = -1;
  let fatCol = -1;

  for (let i = 0; i < tableLines.length; i++) {
    const cells = tableLines[i].split('|').map((c) => c.trim().toLowerCase());
    for (let c = 0; c < cells.length; c++) {
      const cell = cells[c];
      if (calCol === -1 && (cell.includes('کالری') || cell.includes('kcal') || cell.includes('calor') || cell.includes('انرژی'))) {
        calCol = c;
      }
      if (carbCol === -1 && (cell.includes('کربوهیدرات') || cell.includes('کربو') || cell.includes('carb'))) {
        carbCol = c;
      }
      if (protCol === -1 && (cell.includes('پروتئین') || cell.includes('protein') || cell.includes('پروتبوط') || cell.includes('prot'))) {
        protCol = c;
      }
      if (fatCol === -1 && (cell.includes('چربی') || cell.includes('fat') || cell.includes('lipid'))) {
        fatCol = c;
      }
    }
    if (calCol !== -1 || protCol !== -1 || carbCol !== -1 || fatCol !== -1) {
      headerIdx = i;
      break;
    }
  }

  if (headerIdx === -1) {
    return { calories: 0, protein: 0, carbs: 0, fat: 0, found: false };
  }

  // Extract data rows after header
  const dataLines = tableLines.slice(headerIdx + 1).filter((l) => {
    const clean = l.replace(/\|/g, '').trim();
    return clean.length > 0 && !/^[\s\-:]+$/.test(clean);
  });

  if (dataLines.length === 0) {
    return { calories: 0, protein: 0, carbs: 0, fat: 0, found: false };
  }

  // Check for a summary / total row
  let summaryLine: string | null = null;
  for (const line of dataLines) {
    const lower = line.toLowerCase();
    if (
      lower.includes('جمع') ||
      lower.includes('کل') ||
      lower.includes('مجموع') ||
      lower.includes('توتال') ||
      lower.includes('total') ||
      lower.includes('sum')
    ) {
      summaryLine = line;
      break;
    }
  }

  if (summaryLine) {
    const cells = summaryLine.split('|').map((c) => c.trim());
    const calories = calCol !== -1 && cells[calCol] ? parseNumberFromSegment(cells[calCol]) : 0;
    const protein = protCol !== -1 && cells[protCol] ? parseNumberFromSegment(cells[protCol]) : 0;
    const carbs = carbCol !== -1 && cells[carbCol] ? parseNumberFromSegment(cells[carbCol]) : 0;
    const fat = fatCol !== -1 && cells[fatCol] ? parseNumberFromSegment(cells[fatCol]) : 0;

    if (calories > 0 || protein > 0 || carbs > 0 || fat > 0) {
      return {
        calories: Math.round(calories),
        protein: parseFloat(protein.toFixed(1)),
        carbs: parseFloat(carbs.toFixed(1)),
        fat: parseFloat(fat.toFixed(1)),
        found: true,
      };
    }
  }

  // Check if last row is a summary row by comparing value with sum of preceding rows
  if (dataLines.length > 1) {
    const lastLine = dataLines[dataLines.length - 1];
    const lastCells = lastLine.split('|').map((c) => c.trim());
    const lastCal = calCol !== -1 && lastCells[calCol] ? parseNumberFromSegment(lastCells[calCol]) : 0;
    const lastProt = protCol !== -1 && lastCells[protCol] ? parseNumberFromSegment(lastCells[protCol]) : 0;
    const lastCarb = carbCol !== -1 && lastCells[carbCol] ? parseNumberFromSegment(lastCells[carbCol]) : 0;
    const lastFat = fatCol !== -1 && lastCells[fatCol] ? parseNumberFromSegment(lastCells[fatCol]) : 0;

    let precedingCalSum = 0;
    for (let k = 0; k < dataLines.length - 1; k++) {
      const lineLower = dataLines[k].toLowerCase();
      if (lineLower.includes('100g') || lineLower.includes('100 گرم') || /\b100\b/.test(lineLower)) continue;
      const c = dataLines[k].split('|').map((cell) => cell.trim());
      if (calCol !== -1 && c[calCol]) precedingCalSum += parseNumberFromSegment(c[calCol]);
    }

    if (lastCal > 0 && (Math.abs(lastCal - precedingCalSum) <= 80 || lastCal >= precedingCalSum * 0.7)) {
      return {
        calories: Math.round(lastCal),
        protein: parseFloat(lastProt.toFixed(1)),
        carbs: parseFloat(lastCarb.toFixed(1)),
        fat: parseFloat(lastFat.toFixed(1)),
        found: true,
      };
    }
  }

  // If no summary line found, sum up non-reference individual item rows
  let sumCal = 0;
  let sumProt = 0;
  let sumCarb = 0;
  let sumFat = 0;

  for (const line of dataLines) {
    const lineLower = line.toLowerCase();
    // Exclude 100g reference rows when summing
    if (lineLower.includes('100g') || lineLower.includes('100 گرم') || /\b100\b/.test(lineLower)) continue;

    const cells = line.split('|').map((c) => c.trim());
    if (calCol !== -1 && cells[calCol]) sumCal += parseNumberFromSegment(cells[calCol]);
    if (protCol !== -1 && cells[protCol]) sumProt += parseNumberFromSegment(cells[protCol]);
    if (carbCol !== -1 && cells[carbCol]) sumCarb += parseNumberFromSegment(cells[carbCol]);
    if (fatCol !== -1 && cells[fatCol]) sumFat += parseNumberFromSegment(cells[fatCol]);
  }

  if (sumCal > 0 || sumProt > 0 || sumCarb > 0 || sumFat > 0) {
    return {
      calories: Math.round(sumCal),
      protein: parseFloat(sumProt.toFixed(1)),
      carbs: parseFloat(sumCarb.toFixed(1)),
      fat: parseFloat(sumFat.toFixed(1)),
      found: true,
    };
  }

  return { calories: 0, protein: 0, carbs: 0, fat: 0, found: false };
}

/**
 * Parses key-value or bullet point lists from AI response.
 */
function parseKeyValueNutrition(textInEnglish: string): {
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  found: boolean;
} {
  const lines = textInEnglish.split(/\r?\n/);

  let calories = 0;
  let protein = 0;
  let carbs = 0;
  let fat = 0;

  for (const rawLine of lines) {
    const line = rawLine.toLowerCase();

    if (!calories && (line.includes('کالری') || line.includes('kcal') || line.includes('انرژی') || line.includes('calories'))) {
      const val = extractValueFromLine(line);
      if (val > 0) calories = val;
    }

    if (!protein && (line.includes('پروتئین') || line.includes('protein') || line.includes('پروتبوط'))) {
      const val = extractValueFromLine(line);
      if (val > 0) protein = val;
    }

    if (!carbs && (line.includes('کربوهیدرات') || line.includes('کربو') || line.includes('carbs') || line.includes('carbohydrate'))) {
      const val = extractValueFromLine(line);
      if (val > 0) carbs = val;
    }

    if (!fat && (line.includes('چربی') || line.includes('fat') || line.includes('lipids'))) {
      const val = extractValueFromLine(line);
      if (val > 0) fat = val;
    }
  }

  if (calories > 0 || protein > 0 || carbs > 0 || fat > 0) {
    return {
      calories: Math.round(calories),
      protein: parseFloat(protein.toFixed(1)),
      carbs: parseFloat(carbs.toFixed(1)),
      fat: parseFloat(fat.toFixed(1)),
      found: true,
    };
  }

  return { calories: 0, protein: 0, carbs: 0, fat: 0, found: false };
}

/**
 * Extracts a numeric value (handling ranges like 450-550) for given nutrient keywords.
 */
function extractNutrientNumber(textInEnglish: string, keywords: string[]): number {
  const lines = textInEnglish.split(/\r?\n/);

  // Priority 1: Summary or serving lines (containing total / بشقاب / سرویس / پرس / جمع کل / توتال)
  for (const rawLine of lines) {
    const line = rawLine.toLowerCase();
    const isServingOrSummary =
      line.includes('مجموعاً') ||
      line.includes('در مجموع') ||
      line.includes('جمع کل') ||
      line.includes('بشقاب') ||
      line.includes('یک سرویس') ||
      line.includes('یک پرس') ||
      line.includes('کل غذا') ||
      line.includes('توتال');
    const hasKeyword = keywords.some((kw) => line.includes(kw));

    if (isServingOrSummary && hasKeyword) {
      const val = extractValueFromLine(line);
      if (val > 0) return val;
    }
  }

  // Priority 2: Non-100g lines containing keywords
  for (const rawLine of lines) {
    const line = rawLine.toLowerCase();
    const hasKeyword = keywords.some((kw) => line.includes(kw));
    if (!hasKeyword) continue;

    if (line.includes('100g') || line.includes('100 گرم') || line.includes('۱۰۰ گرم')) {
      continue;
    }

    const val = extractValueFromLine(line);
    if (val > 0) return val;
  }

  // Priority 3: Fallback line including 100g reference if nothing else found
  for (const rawLine of lines) {
    const line = rawLine.toLowerCase();
    const hasKeyword = keywords.some((kw) => line.includes(kw));
    if (!hasKeyword) continue;

    const val = extractValueFromLine(line);
    if (val > 0) return val;
  }

  // Priority 4: Global regex fallback
  const keywordPattern = keywords.join('|');
  const globalPattern = new RegExp(
    `(?:${keywordPattern})[^\\d]*(\\d+(?:\\.\\d+)?)\\s*(?:-|–|—|تا|الی|to|\\s+)?\\s*(\\d+(?:\\.\\d+)?)?`,
    'i'
  );
  const match = textInEnglish.toLowerCase().match(globalPattern);
  if (match) {
    const v1 = parseFloat(match[1]);
    const v2 = match[2] ? parseFloat(match[2]) : NaN;
    if (!isNaN(v1) && !isNaN(v2)) {
      return Math.round((v1 + v2) / 2);
    } else if (!isNaN(v1)) {
      return Math.round(v1);
    }
  }

  return 0;
}

export interface WorkerAiFullResponse {
  reply: string;
  provider?: string;
  model?: string;
  debug?: {
    provider?: string;
    model?: string;
    responseTime?: number;
  };
}

// In-flight active request map for preventing duplicate concurrent AI calls
const activeWorkerAiRequests = new Map<string, Promise<WorkerAiFullResponse>>();

/**
 * Sends AI request exclusively to the external AI endpoint and returns full response object with debug info.
 */
export async function sendWorkerAiFullRequest(
  feature: 'rehabilitation' | 'nutrition' | 'learning_assistant' | string,
  message: string,
  history: Array<{ role: string; content: string }> = []
): Promise<WorkerAiFullResponse> {
  // Check online status first
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    throw new Error('برای استفاده از دستیار هوش مصنوعی نیاز به اتصال اینترنت دارید.');
  }

  // Complete message payload preserved without artificial length limits
  const safeMessage = (message || '').trim();

  // Complete history preserved without artificial length limits
  const safeHistory = (history || []).map((item) => ({
    role: item.role === 'assistant' ? 'assistant' : 'user',
    content: (item.content || '').trim(),
  }));

  // Deduplication check: if an identical request is currently in-flight, reuse its Promise
  const requestKey = `${feature}:${safeMessage}:${JSON.stringify(safeHistory)}`;
  if (activeWorkerAiRequests.has(requestKey)) {
    console.warn(`[AI Request Guard] Duplicate request detected for feature "${feature}". Reusing active request.`);
    return activeWorkerAiRequests.get(requestKey)!;
  }

  const executeRequest = async (): Promise<WorkerAiFullResponse> => {
    const startTime = Date.now();

    try {
      const apiRes = await apiPost('/api/ai/chat', {
        feature,
        message: safeMessage,
        history: safeHistory,
      });

      if (!apiRes.success || apiRes.error) {
        const rawErr = JSON.stringify(apiRes.error || apiRes || '');
        if (rawErr.includes('rate_limit') || rawErr.includes('RESOURCE_EXHAUSTED') || rawErr.includes('Limit') || rawErr.includes('quota')) {
          throw new Error('محدودیت تعداد درخواست یا توکن رخ داده است. لطفاً چند لحظه صبر کنید و سپس دوباره تلاش کنید.');
        }
        const errMsg =
          (apiRes as any)?.error?.error?.message ||
          apiRes.error ||
          'خطای سرویس هوش مصنوعی';
        throw new Error(errMsg);
      }

      const data = apiRes as any;

      if (!data.reply) {
        throw new Error('پاسخی از سرویس هوش مصنوعی دریافت نشد.');
      }

      const elapsed = Date.now() - startTime;
      const providerStr = data.provider || data.debug?.provider || undefined;
      const modelStr = data.model || data.debug?.model || undefined;
      const responseTimeMs = typeof data.debug?.responseTime === 'number' ? data.debug.responseTime : elapsed;

      return {
        reply: data.reply,
        provider: providerStr,
        model: modelStr,
        debug: {
          provider: providerStr,
          model: modelStr,
          responseTime: responseTimeMs,
        },
      };
    } catch (err: any) {
      console.error('AI Request Failed:', err);
      if (
        err.name === 'TypeError' ||
        (err.message && err.message.toLowerCase().includes('failed to fetch'))
      ) {
        throw new Error('برای استفاده از دستیار هوش مصنوعی نیاز به اتصال اینترنت دارید.');
      }
      throw err;
    }
  };

  const requestPromise = executeRequest();
  activeWorkerAiRequests.set(requestKey, requestPromise);

  try {
    return await requestPromise;
  } finally {
    activeWorkerAiRequests.delete(requestKey);
  }
}

/**
 * Sends AI request exclusively to the external AI endpoint.
 * Returns reply string.
 */
export async function sendWorkerAiRequest(
  feature: 'rehabilitation' | 'nutrition' | 'learning_assistant' | string,
  message: string,
  history: Array<{ role: string; content: string }> = []
): Promise<string> {
  const fullResponse = await sendWorkerAiFullRequest(feature, message, history);
  return fullResponse.reply;
}

/**
 * Estimates food nutrients locally using built-in Iranian food database rules.
 * Serves as immediate fallback and sanity check against AI prompt variations.
 */
export function estimateLocalNutrients(name: string, quantity: string = ""): { calories: number; protein: number; carbs: number; fat: number } {
  const farsiToEnglishDigits = (str: string): string => {
    const farsiDigits = [/۰/g, /۱/g, /۲/g, /۳/g, /۴/g, /۵/g, /۶/g, /۷/g, /۸/g, /۹/g];
    const englishDigits = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
    let result = str;
    for (let i = 0; i < 10; i++) {
      result = result.replace(farsiDigits[i], englishDigits[i]);
    }
    return result;
  };

  const rawInput = farsiToEnglishDigits((name + " " + quantity).trim().toLowerCase());

  // Extract explicit overall weight if mentioned (e.g. 400g / 400 گرم)
  const totalWeightMatch = rawInput.match(/(\d+(?:\.\d+)?)\s*(?:gram|g|گرم)/i);
  const totalWeight = totalWeightMatch ? parseFloat(totalWeightMatch[1]) : null;

  // Check if it's a mixed Iranian dish with overall weight
  const isMixedIranianDish = (rawInput.includes('پلو') || rawInput.includes('برنج') || rawInput.includes('چلو')) &&
    (rawInput.includes('گوشت') || rawInput.includes('مرغ') || rawInput.includes('کباب') || rawInput.includes('لوبیا') || rawInput.includes('خورشت') || rawInput.includes('ته دیگ'));

  if (totalWeight && totalWeight >= 100 && isMixedIranianDish) {
    // Cooked mixed Iranian rice/meat/stew dishes average ~185 kcal per 100g
    const totalCal = Math.round((185 * totalWeight) / 100);
    const totalProt = parseFloat(((9.2 * totalWeight) / 100).toFixed(1));
    const totalCarb = parseFloat(((20 * totalWeight) / 100).toFixed(1));
    const totalFat = parseFloat(((5.5 * totalWeight) / 100).toFixed(1));
    return { calories: totalCal, protein: totalProt, carbs: totalCarb, fat: totalFat };
  }

  // Remove meta header words like "400 گرم کل غذا" before splitting items
  const cleanedForItems = rawInput
    .replace(/\b\d+\s*(?:gram|g|گرم)\s*(?:کل\s*غذا|کل|توتال)?/gi, '')
    .replace(/کل\s*غذا/gi, '')
    .trim();

  const parts = cleanedForItems.split(/\s+(?:و|\+|,|and)\s+/).filter(Boolean);

  let totalCalories = 0;
  let totalProtein = 0;
  let totalCarbs = 0;
  let totalFat = 0;

  for (const part of parts) {
    const norm = part.trim();
    if (!norm) continue;

    const weightMatch = norm.match(/(\d+(?:\.\d+)?)\s*(?:gram|g|گرم)/i);
    const weight = weightMatch ? parseFloat(weightMatch[1]) : null;

    let itemCalories = 0;
    let itemProtein = 0;
    let itemCarbs = 0;
    let itemFat = 0;

    if (norm.includes("کیک") || norm.includes("cake") || norm.includes("شیرینی") || norm.includes("شکلات")) {
      if (weight !== null) {
        itemCalories = (380 * weight) / 100;
        itemProtein = (5 * weight) / 100;
        itemCarbs = (50 * weight) / 100;
        itemFat = (18 * weight) / 100;
      } else {
        itemCalories = 280; itemProtein = 3; itemCarbs = 35; itemFat = 14;
      }
    } else if (norm.includes("چای") || norm.includes("tea") || norm.includes("قهوه") || norm.includes("coffee")) {
      itemCalories = 2; itemProtein = 0.1; itemCarbs = 0.4; itemFat = 0;
    } else if (norm.includes("نان") || norm.includes("bread") || norm.includes("سنگک") || norm.includes("بربری") || norm.includes("لواش") || norm.includes("تافتون")) {
      if (weight !== null) {
        itemCalories = (260 * weight) / 100;
        itemProtein = (8 * weight) / 100;
        itemCarbs = (52 * weight) / 100;
        itemFat = (2.5 * weight) / 100;
      } else {
        itemCalories = 120; itemProtein = 4; itemCarbs = 24; itemFat = 1.2;
      }
    } else if (norm.includes("برنج") || norm.includes("rice") || norm.includes("پلو") || norm.includes("کته") || norm.includes("چلو")) {
      if (weight !== null) {
        itemCalories = (150 * weight) / 100;
        itemProtein = (3 * weight) / 100;
        itemCarbs = (32 * weight) / 100;
        itemFat = (1 * weight) / 100;
      } else {
        itemCalories = 240; itemProtein = 4.5; itemCarbs = 50; itemFat = 1.5;
      }
    } else if (norm.includes("مرغ") || norm.includes("chicken") || norm.includes("جوجه")) {
      if (weight !== null) {
        itemCalories = (180 * weight) / 100;
        itemProtein = (30 * weight) / 100;
        itemCarbs = 0;
        itemFat = (5 * weight) / 100;
      } else {
        itemCalories = 200; itemProtein = 35; itemCarbs = 0; itemFat = 5;
      }
    } else if (norm.includes("گوشت") || norm.includes("meat") || norm.includes("کباب") || norm.includes("کوبیده")) {
      if (weight !== null) {
        itemCalories = (260 * weight) / 100;
        itemProtein = (26 * weight) / 100;
        itemCarbs = 0;
        itemFat = (17 * weight) / 100;
      } else {
        itemCalories = 270; itemProtein = 26; itemCarbs = 0; itemFat = 18;
      }
    } else if (norm.includes("ته دیگ") || norm.includes("سیب زمینی") || norm.includes("potato")) {
      if (weight !== null) {
        itemCalories = (220 * weight) / 100;
        itemProtein = (3 * weight) / 100;
        itemCarbs = (30 * weight) / 100;
        itemFat = (10 * weight) / 100;
      } else {
        itemCalories = 150; itemProtein = 2; itemCarbs = 20; itemFat = 7;
      }
    } else if (norm.includes("لوبیا") || norm.includes("عدس") || norm.includes("حبوبات")) {
      if (weight !== null) {
        itemCalories = (140 * weight) / 100;
        itemProtein = (9 * weight) / 100;
        itemCarbs = (22 * weight) / 100;
        itemFat = (1 * weight) / 100;
      } else {
        itemCalories = 120; itemProtein = 8; itemCarbs = 20; itemFat = 1;
      }
    } else {
      if (weight !== null) {
        itemCalories = (180 * weight) / 100;
        itemProtein = (6 * weight) / 100;
        itemCarbs = (20 * weight) / 100;
        itemFat = (5 * weight) / 100;
      } else {
        itemCalories = 120; itemProtein = 4; itemCarbs = 18; itemFat = 3;
      }
    }

    totalCalories += itemCalories;
    totalProtein += itemProtein;
    totalCarbs += itemCarbs;
    totalFat += itemFat;
  }

  return {
    calories: Math.round(totalCalories || 300),
    protein: parseFloat(totalProtein.toFixed(1)),
    carbs: parseFloat(totalCarbs.toFixed(1)),
    fat: parseFloat(totalFat.toFixed(1))
  };
}

/**
 * Builds a standardized, structured prompt for nutrition estimation.
 * Forces the AI to break down multi-component meals (e.g. 400g total), calculate sums for the entire portion,
 * and provide a clean JSON block at the bottom for instant extraction.
 */
export function buildNutritionPrompt(userInput: string, quantityStr: string = ''): string {
  const cleanInput = userInput.trim();
  const cleanQty = quantityStr.trim();
  const fullFoodDesc = cleanQty && cleanQty !== '۱ وعده' && !cleanInput.includes(cleanQty)
    ? `${cleanInput} (${cleanQty})`
    : cleanInput;

  return `ارزش غذایی دقیق وعده زیر را ارزیابی و محاسبه نمایید:
"${fullFoodDesc}"

قوانین و دستورالعمل‌های حیاتی برای محاسبه هوش مصنوعی:
۱. تمام درشتمغذی‌ها (پروتئین، کربوهیدرات، چربی) باید الزاماً به **گرم (g)** گزارش شوند.
۲. کالری باید الزاماً به **کیلوکالری (kcal)** باشد.
۳. اگر کاربر وزن یا مقداری مشخص کرده است (مثلاً ۴۰۰ گرم)، تمام اعداد خروجی (کالری، پروتئین، کربوهیدرات و چربی) باید دقیقاً برای **کل مقدار اعلام‌شده (کل ۴۰۰ گرم)** باشند، نه برای ۱۰۰ گرم مرجع.
۴. از دادن محدوده اعداد (مانند ۳-۴ گرم) خودداری فرموده و عدد دقیق اعلام کنید.
۵. در انتهای پاسخ حتماً خطوط استاندارد زیر و کد JSON معتبر قرار دهید:

The total calory of this Food is = [کالری کل]
Total Protein = [پروتئین کل]
Total Carbs = [کربوهیدرات کل]
Total Fat = [چربی کل]

\`\`\`json
{
  "foodName": "نام دقیق غذا",
  "quantity": "وزن/مقدار کل",
  "calories": 725,
  "protein": 36.5,
  "carbs": 78.5,
  "fat": 22.0
}
\`\`\``;
}

/**
 * Sanitizes and validates parsed nutrition results against unit hallucinations (mg/kJ)
 * and portion scaling errors (100g vs total meal weight).
 */
function sanitizeAndValidateNutrition(raw: ParsedFoodNutrition, userPrompt: string): ParsedFoodNutrition {
  let { calories, protein, carbs, fat, foodName, quantity, notes, rawReply } = raw;

  // 1. Sanitize protein, carbs, fat if in milligrams (>300)
  if (protein > 300) protein = parseFloat((protein / 1000).toFixed(1));
  if (carbs > 500) carbs = parseFloat((carbs / 1000).toFixed(1));
  if (fat > 300) fat = parseFloat((fat / 1000).toFixed(1));

  // 2. Extract total weight from user prompt if mentioned (e.g. 400g / 400 گرم)
  const normPrompt = convertPersianDigitsToEnglish(userPrompt);
  const weightMatch = normPrompt.match(/(\d+(?:\.\d+)?)\s*(?:gram|g|گرم)/i);
  const totalWeight = weightMatch ? parseFloat(weightMatch[1]) : null;

  // 3. Clean up quantity if it extracted garbage like "3-4 گرم" when user prompt has total weight
  if (totalWeight && (quantity.includes('-') || !quantity.includes(String(totalWeight)))) {
    quantity = `${totalWeight} گرم`;
  }

  // 4. Calculate expected calories from macros (P*4 + C*4 + F*9)
  let macroCal = Math.round(protein * 4 + carbs * 4 + fat * 9);

  // 5. If user specified a large total weight (e.g. >= 200g) but macroCal is very low (< 220 kcal),
  // then AI returned 100g reference values instead of total meal values. Scale macros to total weight!
  if (totalWeight && totalWeight >= 200 && macroCal > 0 && macroCal < 220) {
    const scaleFactor = totalWeight / 100;
    protein = parseFloat((protein * scaleFactor).toFixed(1));
    carbs = parseFloat((carbs * scaleFactor).toFixed(1));
    fat = parseFloat((fat * scaleFactor).toFixed(1));
    macroCal = Math.round(protein * 4 + carbs * 4 + fat * 9);
  }

  // 6. Enforce strict mathematical macro-to-calorie agreement
  if (macroCal > 0) {
    if (calories <= 0 || calories > 3500 || Math.abs(calories - macroCal) > macroCal * 0.15) {
      calories = macroCal;
    }
  }

  // 7. Final fallback if results are still unrealistic or empty
  if (calories <= 30 || calories > 3000 || (protein === 0 && carbs === 0 && fat === 0)) {
    const local = estimateLocalNutrients(userPrompt, quantity);
    calories = local.calories;
    protein = local.protein;
    carbs = local.carbs;
    fat = local.fat;
  }

  return {
    foodName,
    calories,
    protein,
    carbs,
    fat,
    quantity,
    notes,
    rawReply,
  };
}

/**
 * Parses the worker reply into structured nutrition data.
 */
export function parseNutritionReply(replyText: string, userPrompt: string = 'وعده غذایی'): ParsedFoodNutrition {
  let cleanText = replyText.trim();
  const foodName = extractCleanFoodName(userPrompt);

  // Convert digits for robust pattern matching
  const englishText = convertPersianDigitsToEnglish(cleanText);

  let rawResult: ParsedFoodNutrition | null = null;

  // 0. Direct match for standard "The total calory of this Food is = [NUMBER]" pattern
  const totalCalMatch = englishText.match(/The total calor(?:y|ies) of this [fF]ood is\s*=\s*(\d+(?:\.\d+)?)/i) ||
                        cleanText.match(/(?:ارزش غذایی کل|کالری کل|جمع کل کالری)[^:\d]*[:=]?\s*(\d+)/i);
  if (totalCalMatch) {
    const extractedCal = Math.round(parseFloat(totalCalMatch[1]));
    if (extractedCal > 0) {
      const protMatch = englishText.match(/Total Protein\s*=\s*(\d+(?:\.\d+)?)/i) ||
                        englishText.match(/پروتئین کل[^:\d]*[:=]?\s*(\d+(?:\.\d+)?)/i);
      const carbMatch = englishText.match(/Total Carbs\s*=\s*(\d+(?:\.\d+)?)/i) ||
                        englishText.match(/کربوهیدرات کل[^:\d]*[:=]?\s*(\d+(?:\.\d+)?)/i);
      const fatMatch = englishText.match(/Total Fat\s*=\s*(\d+(?:\.\d+)?)/i) ||
                        englishText.match(/چربی کل[^:\d]*[:=]?\s*(\d+(?:\.\d+)?)/i);

      rawResult = {
        foodName,
        calories: extractedCal,
        protein: protMatch ? parseFloat(parseFloat(protMatch[1]).toFixed(1)) : parseFloat(extractNutrientNumber(englishText, ['پروتئین', 'protein', 'prot']).toFixed(1)),
        carbs: carbMatch ? parseFloat(parseFloat(carbMatch[1]).toFixed(1)) : parseFloat(extractNutrientNumber(englishText, ['کربوهیدرات', 'carbs', 'carbohydrate']).toFixed(1)),
        fat: fatMatch ? parseFloat(parseFloat(fatMatch[1]).toFixed(1)) : parseFloat(extractNutrientNumber(englishText, ['چربی', 'fat']).toFixed(1)),
        quantity: extractCleanQuantity(userPrompt, englishText) || '۱ وعده',
        notes: replyText,
        rawReply: replyText,
      };
    }
  }

  // 1. Attempt direct JSON parse (search from bottom to top so final summary JSON is picked first)
  if (!rawResult) {
    try {
      const jsonBlocks = cleanText.match(/\{[\s\S]*?\}/g) || [];
      for (const block of jsonBlocks.reverse()) {
        try {
          const parsed = JSON.parse(block);
          if (parsed && (typeof parsed.calories !== 'undefined' || typeof parsed.protein !== 'undefined')) {
            const cal = Math.round(Number(parsed.calories) || 0);
            if (cal > 0) {
              rawResult = {
                foodName: parsed.foodName || parsed.name || foodName,
                calories: cal,
                protein: Number(parsed.protein) || 0,
                carbs: Number(parsed.carbs) || 0,
                fat: Number(parsed.fat) || 0,
                quantity: parsed.quantity || parsed.servingSize || extractCleanQuantity(userPrompt, cleanText) || '۱ وعده',
                notes: parsed.notes || parsed.description || replyText,
                rawReply: replyText,
              };
              break;
            }
          }
        } catch (e) {
          // Continue to next JSON block
        }
      }
    } catch (e) {
      // Ignore JSON error
    }
  }

  const quantity = extractCleanQuantity(userPrompt, englishText) || '۱ وعده';

  // 2. Attempt Markdown Table parsing
  if (!rawResult) {
    const tableResult = parseTableNutrition(englishText);
    if (tableResult.found && tableResult.calories > 0) {
      rawResult = {
        foodName,
        calories: tableResult.calories,
        protein: tableResult.protein,
        carbs: tableResult.carbs,
        fat: tableResult.fat,
        quantity,
        notes: replyText,
        rawReply: replyText,
      };
    }
  }

  // 3. Attempt Key-Value / Line-by-Line list parsing
  if (!rawResult) {
    const keyValueResult = parseKeyValueNutrition(englishText);
    if (keyValueResult.found && keyValueResult.calories > 0) {
      rawResult = {
        foodName,
        calories: keyValueResult.calories,
        protein: keyValueResult.protein,
        carbs: keyValueResult.carbs,
        fat: keyValueResult.fat,
        quantity,
        notes: replyText,
        rawReply: replyText,
      };
    }
  }

  // 4. Fallback to global regex extraction
  if (!rawResult) {
    const calories = Math.round(extractNutrientNumber(englishText, ['کالری', 'کیلوکالری', 'kcal', 'calories', 'انرژی']));
    const protein = parseFloat(extractNutrientNumber(englishText, ['پروتئین', 'پروتبوط', 'protein', 'prot']).toFixed(1));
    const carbs = parseFloat(extractNutrientNumber(englishText, ['کربوهیدرات', 'کربو', 'carbs', 'carbohydrate']).toFixed(1));
    const fat = parseFloat(extractNutrientNumber(englishText, ['چربی', 'fat', 'lipids']).toFixed(1));

    rawResult = {
      foodName,
      calories,
      protein,
      carbs,
      fat,
      quantity,
      notes: replyText,
      rawReply: replyText,
    };
  }

  // Sanitize, validate and recalibrate raw result against milligram / kilojoule / 100g scaling errors
  return sanitizeAndValidateNutrition(rawResult, userPrompt);
}

/**
 * Extracts a clean food name from user prompt or default.
 */
function extractCleanFoodName(userPrompt: string): string {
  const currentLang = getAppLanguage();
  const defaultFallback = currentLang === 'sv' ? 'AI-analyserad måltid' : (currentLang === 'en' ? 'AI Analyzed Meal' : 'وعده تحلیل شده هوش مصنوعی');
  if (!userPrompt) return defaultFallback;

  // Check for quoted name or explicit label
  const quotedMatch = userPrompt.match(/(?:namn|name|نام)\s*(?:ghaza|mat|food|غذا)?\s*:\s*["'«]([^"'»]+)["'»]/i)
    || userPrompt.match(/["'«]([^"'»]+)["'»]/);
  if (quotedMatch && quotedMatch[1].trim()) {
    return quotedMatch[1].trim();
  }

  // Strip system prefixes
  let text = userPrompt.replace(/^.*(?:پیام کاربر|User Prompt|Användarmeddelande):\s*/s, '').trim();
  text = text.replace(/^(?:محاسبه ارزش غذایی|Näringsberäkning|Nutrition calculation)[^\n:]*:\s*/i, '').trim();

  // Remove leading explicit total weight/quantity in Swedish (e.g. "en tallrik kyckling", "2 portioner ris", "150g lax")
  text = text.replace(/^(?:en|ett|\d+)\s*(?:tallrik|portion(?:er)?|skiva|skivor|glas|kopp(?:ar)?|burk(?:ar)?|påse|st|styck|msk|matsked|tsk|tesked|g|gram|kg|dl|cl|ml)\s*(?:av|med)?\s*/i, '').trim();

  // Remove leading explicit total weight/quantity in English (e.g. "a plate of chicken", "1 serving of rice", "150g salmon")
  text = text.replace(/^(?:a|an|one|two|three|four|\d+)\s*(?:plate|plates|serving|servings|slice|slices|glass|glasses|cup|cups|bowl|bowls|piece|pieces|can|cans|tbsp|tsp|g|gram|grams|kg|ml|oz)\s*(?:of|with)?\s*/i, '').trim();

  // Remove leading explicit total weight if formatted like "۴۰۰ گرم کل غذا ، " or "150g "
  text = text.replace(/^(\d+|یک|دو|سه|چهار|پنج)\s*(?:گرم|بشقاب|سیخ|لیوان|کاسه|عدد|دست|وعده|پرسی|پُرس|سرویس|g)\s*(?:کل\s*غذا)?\s*[\،\,-]?\s*/i, '').trim();

  text = text.split('\n')[0].trim();

  if (text.length > 60) {
    text = text.substring(0, 60) + '...';
  }

  return text || defaultFallback;
}

/**
 * Extracts serving quantity from prompt or text.
 */
function extractCleanQuantity(userPrompt: string, englishText: string = ''): string {
  const currentLang = getAppLanguage();
  const cleanPrompt = userPrompt.replace(/^.*(?:پیام کاربر|User Prompt|Användarmeddelande):\s*/s, '').trim();

  // 1. Swedish prompt match (e.g. "en tallrik kyckling", "2 portioner", "150 g lax", "2 st ägg")
  const svPromptMatch = cleanPrompt.match(/(?:en|ett|\d+)\s*(?:tallrik|portion(?:er)?|skiva|skivor|glas|kopp(?:ar)?|burk(?:ar)?|påse|st|styck|msk|matsked|tsk|tesked|g|gram|kg|dl|cl|ml)/i);
  if (svPromptMatch) {
    let q = svPromptMatch[0].trim();
    if (q.toLowerCase() === 'en talrik' || q.toLowerCase() === 'en tallrik') {
      return currentLang === 'sv' ? '1 portion (en tallrik)' : (currentLang === 'en' ? '1 plate (1 serving)' : '۱ بشقاب (۱ پرس)');
    }
    return q;
  }

  // 2. English prompt match (e.g. "1 plate of chicken", "1 serving", "200g", "2 slices")
  const enPromptMatch = cleanPrompt.match(/(?:a|an|one|two|three|four|\d+)\s*(?:plate|plates|serving|servings|slice|slices|glass|glasses|cup|cups|bowl|bowls|piece|pieces|can|cans|tbsp|tsp|g|gram|grams|kg|ml|oz)/i);
  if (enPromptMatch) {
    let q = enPromptMatch[0].trim();
    if (q.toLowerCase() === 'a plate' || q.toLowerCase() === '1 plate' || q.toLowerCase() === 'one plate') {
      return currentLang === 'sv' ? '1 portion (en tallrik)' : (currentLang === 'en' ? '1 plate (1 serving)' : '۱ بشقاب (۱ پرس)');
    }
    return q;
  }

  // 3. Persian prompt match (e.g. "400 گرم", "150 گرم مرغ", "2 بشقاب")
  const promptMatch = cleanPrompt.match(/(\d+|یک|دو|سه|چهار|پنج)\s*(?:گرم|بشقاب|سیخ|لیوان|کاسه|عدد|دست|وعده|پرسی|پُرس|سرویس|g)/i);
  if (promptMatch) {
    return promptMatch[0].trim();
  }

  // 4. Look in response text for full serving descriptors
  const servingMatch = englishText.match(/(\d+(?:-|–|—|تا)\d+\s*گرم|\d+\s*گرم|\d+\s*بشقاب|یک\s*بشقاب|یک\s*سرویس|\d+\s*سرویس|یک\s*پرس|\d+\s*portion(?:er)?|\d+\s*servings?|\d+\s*g(?:ram)?)/i);
  if (servingMatch) {
    if (servingMatch[0].includes('100') || servingMatch[0].includes('۱۰۰')) {
      const fullServingMatch = englishText.match(/(?:سرویس|وعده|بشقاب|portion|serving)[^\n|]*?(\d+(?:-|–|—|تا)\d+\s*گرم|\d+\s*گرم|یک\s*بشقاب|\d+\s*g)/i)
        || englishText.match(/(\d+(?:-|–|—|تا)\d+\s*گرم|یک\s*بشقاب|یک\s*سرویس|\d+\s*portion|\d+\s*serving)/gi)?.find(s => !s.includes('100'));
      if (fullServingMatch) {
        return typeof fullServingMatch === 'string' ? fullServingMatch.trim() : fullServingMatch[0].trim();
      }
    } else {
      return servingMatch[0].trim();
    }
  }

  // Fallback based on selected language
  if (currentLang === 'sv') {
    return '1 portion (en tallrik)';
  }
  if (currentLang === 'en') {
    return '1 serving (1 plate)';
  }
  return '۱ پرس (یک بشقاب)';
}

/**
 * Cleanly strips raw markdown syntax (headers, bold, lists, tables)
 * and produces a concise short summary for task instructions.
 */
export function stripMarkdownAndSummarize(str: string, maxLen: number = 220): string {
  if (!str) return '';
  let clean = str
    .replace(/```[\s\S]*?```/g, '') // remove code blocks
    .replace(/^\|.*\|$/gm, '') // remove markdown table lines
    .replace(/\|/g, ' ') // remove stray pipe characters
    .replace(/^#+\s*/gm, '') // remove markdown headers (#, ##, ###)
    .replace(/\*{1,2}/g, '') // remove * and **
    .replace(/_{1,2}/g, '') // remove _ and __
    .replace(/^>\s*/gm, '') // remove blockquotes
    .replace(/^-{3,}/gm, '') // remove hr lines
    .replace(/`{1,3}/g, '') // remove backticks
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // links
    .replace(/^[\s\-*\d+\.]+\s+/gm, '') // list markers
    .replace(/\n+/g, ' ') // replace line breaks with spaces
    .replace(/\s{2,}/g, ' ') // collapse spaces
    .trim();

  // If text contains specific instruction section, try to extract it
  if (clean.length > maxLen) {
    const match = clean.match(/(?:دستورالعمل|نحوه انجام|روش انجام|شرح حرکت|تمرین|کشش)\s*:\s*([^.]+)/i);
    if (match && match[1] && match[1].trim().length > 15) {
      clean = match[1].trim();
    }
  }

  // Cap cleanly at maxLen
  if (clean.length > maxLen) {
    const truncated = clean.substring(0, maxLen);
    const lastPunctuation = Math.max(truncated.lastIndexOf('.'), truncated.lastIndexOf('؛'), truncated.lastIndexOf('،'));
    if (lastPunctuation > maxLen * 0.4) {
      clean = truncated.substring(0, lastPunctuation + 1);
    } else {
      const lastSpace = truncated.lastIndexOf(' ');
      clean = (lastSpace > 0 ? truncated.substring(0, lastSpace) : truncated) + '...';
    }
  }

  return clean;
}

/**
 * Parses the worker reply into structured rehabilitation exercise data.
 */
export function parseRehabReply(replyText: string, userPainInput: string = ''): ParsedRehabExercise {
  let cleanText = replyText.trim();
  if (cleanText.startsWith('```')) {
    cleanText = cleanText.replace(/^```(?:json)?\s*/i, '');
    cleanText = cleanText.replace(/\s*```$/, '').trim();
  }

  try {
    const jsonMatch = cleanText.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      const parsed = JSON.parse(jsonMatch[0]);
      if (parsed && (parsed.name || parsed.description || parsed.title)) {
        const rawName = parsed.name || parsed.title || 'تمرین کششی توانبخشی';
        const rawDesc = parsed.description || parsed.instructions || cleanText;
        const rawPurpose = parsed.purpose || parsed.benefits || 'کاهش فشار و التیام عضلات';
        const rawTarget = parsed.targetMuscles || parsed.target_muscles || userPainInput || 'عضلات فشرده و ستون فقرات';
        const rawSafety = parsed.safetyNotes || parsed.safety_notes || 'در صورت احساس درد شدید تمرین را متوقف کنید.';

        return {
          name: stripMarkdownAndSummarize(rawName, 60),
          category: 'rehab',
          description: stripMarkdownAndSummarize(rawDesc, 220),
          purpose: stripMarkdownAndSummarize(rawPurpose, 100),
          targetMuscles: stripMarkdownAndSummarize(rawTarget, 80),
          sets: Number(parsed.sets) || 3,
          reps: parsed.reps || '۳ تکرار، ۳۰ ثانیه مکث',
          rest: parsed.rest || '۲۰ ثانیه',
          duration: Number(parsed.duration) || 4,
          difficulty: parsed.difficulty || 'Beginner',
          safetyNotes: stripMarkdownAndSummarize(rawSafety, 120),
          rawReply: replyText,
        };
      }
    }
  } catch (e) {
    // Ignore
  }

  // Convert text response to exercise object
  const titleMatch = cleanText.match(/(?:نام حرکت|عنوان|تمرین|Exercise):\s*(.+)/i) || cleanText.match(/^#+\s*(.+)/m);
  const rawName = titleMatch ? titleMatch[1].trim() : 'تمرین کششی توانبخشی';

  return {
    name: stripMarkdownAndSummarize(rawName, 60),
    category: 'rehab',
    description: stripMarkdownAndSummarize(cleanText, 220),
    purpose: 'تسکین درد و بهبود دامنه حرکتی',
    targetMuscles: stripMarkdownAndSummarize(userPainInput, 80) || 'ستون فقرات و عضلات درگیر',
    sets: 3,
    reps: '۳ تکرار با ۳۰ ثانیه مکث',
    rest: '۲۰ ثانیه',
    duration: 4,
    difficulty: 'Beginner',
    safetyNotes: 'در صورت احساس درد شدید غیرمعمول، انجام تمرین را متوقف نمایید.',
    rawReply: replyText,
  };
}
